import React, { useEffect, useRef, useState } from 'react';
import {
  CandlestickSeries,
  ColorType,
  HistogramSeries,
  LineSeries,
  createChart,
} from 'lightweight-charts';
import { apiFetch } from './api';

const MA_CONFIG = [
  // Weighted by how much attention each line deserves: the 10MA is the one the
  // screen is built around, so it stays thin but near-black; the 20 and 50 fade
  // back so they read as context rather than competing with it.
  { period: 10, color: 'rgba(61, 31, 0, 0.95)' },
  { period: 20, color: 'rgba(153, 0, 255, 0.55)' },
  { period: 50, color: 'rgba(25, 118, 210, 0.32)' },
];

/**
 * The moving average of the visible candles, computed with the leading
 * warm-up bars included so the line is complete from the very first bar drawn,
 * then clipped back to the visible window.
 */
function movingAverageOverWarmup(payload, period) {
  const visible = payload.candles || [];
  const warmup = payload.warmup || [];
  if (!visible.length) return [];
  const points = simpleMovingAverage([...warmup, ...visible], period);
  const firstVisible = visible[0].time;
  return points.filter((point) => point.time >= firstVisible);
}

function simpleMovingAverage(candles, period) {
  let rollingTotal = 0;
  return candles.reduce((points, candle, index) => {
    rollingTotal += candle.close;
    if (index >= period) rollingTotal -= candles[index - period].close;
    if (index >= period - 1) {
      points.push({ time: candle.time, value: rollingTotal / period });
    }
    return points;
  }, []);
}

const InteractiveChart = ({
  market,
  dataset = 'av',
  live = false,
  symbol,
  date,
  period,
  pickMode,
  onPricePick,
  entryPrice,
  holdingEntryPrice,
  stopPrice,
  showMovingAverages = true,
  transactionMarkers = [],
  fullHistory = false,
  reviewEndDate = '',
  reviewStartDate = '',
}) => {
  const containerRef = useRef(null);
  const chartRef = useRef(null);
  const candleSeriesRef = useRef(null);
  const maSeriesRef = useRef([]);
  const volumeSeriesRef = useRef(null);
  const stopLineRef = useRef(null);
  const entryLineRef = useRef(null);
  const hasFitOnceRef = useRef(false);
  const pickModeRef = useRef(pickMode);
  const onPricePickRef = useRef(onPricePick);
  const [status, setStatus] = useState('idle');

  // Buy/exit fills drawn on the candles themselves, matching the ChartDirector
  // chart. The two price lines below are a different thing: they track the
  // order form's entry/stop, not where a trade actually filled.
  useEffect(() => {
    const series = candleSeriesRef.current;
    if (!series) return;
    const markers = (transactionMarkers || [])
      .filter((marker) => marker?.date && Number(marker.price) > 0)
      .map((marker) => ({
        time: String(marker.date).slice(0, 10),
        position: marker.kind === 'exit' ? 'aboveBar' : 'belowBar',
        color: marker.kind === 'exit' ? '#d92d20' : '#175cd3',
        shape: marker.kind === 'exit' ? 'arrowDown' : 'arrowUp',
        text: `${marker.label} ${Number(marker.price).toFixed(2)}`,
      }))
      .sort((a, b) => a.time.localeCompare(b.time));
    try {
      series.setMarkers(markers);
    } catch {
      // Older lightweight-charts builds without setMarkers: the chart is still
      // fully usable, it just cannot show fills.
    }
  }, [transactionMarkers, symbol, date, period, dataset]);

  useEffect(() => {
    pickModeRef.current = pickMode;
    onPricePickRef.current = onPricePick;
  }, [pickMode, onPricePick]);

  // Create the chart and its series exactly once. Never torn down and rebuilt
  // on symbol/date/period changes — that's what was causing the visible redraw.
  useEffect(() => {
    if (!containerRef.current) return undefined;

    const chart = createChart(containerRef.current, {
      width: containerRef.current.clientWidth,
      height: containerRef.current.clientHeight,
      layout: {
        background: { type: ColorType.Solid, color: '#ffffff' },
        textColor: '#475467',
        fontFamily: '-apple-system, BlinkMacSystemFont, Segoe UI, sans-serif',
      },
      grid: {
        vertLines: { color: '#f0f2f5' },
        horzLines: { color: '#f0f2f5' },
      },
      rightPriceScale: { borderColor: '#d0d5dd' },
      timeScale: { borderColor: '#d0d5dd', timeVisible: false },
      crosshair: { vertLine: { color: '#98a2b3' }, horzLine: { color: '#98a2b3' } },
    });
    chartRef.current = chart;

    const candleSeries = chart.addSeries(CandlestickSeries, {
      upColor: '#12b76a',
      downColor: '#f04438',
      borderUpColor: '#12b76a',
      borderDownColor: '#f04438',
      wickUpColor: '#12b76a',
      wickDownColor: '#f04438',
    });
    candleSeriesRef.current = candleSeries;

    maSeriesRef.current = MA_CONFIG.map(({ color }) =>
      chart.addSeries(LineSeries, {
        color,
        lineWidth: 1,
        priceLineVisible: false,
        lastValueVisible: false,
        crosshairMarkerVisible: false,
        title: '',
      })
    );

    const volumeSeries = chart.addSeries(HistogramSeries, {
      priceFormat: { type: 'volume' },
      priceScaleId: 'volume',
    });
    volumeSeries.priceScale().applyOptions({ scaleMargins: { top: 0.82, bottom: 0 } });
    volumeSeriesRef.current = volumeSeries;

    chart.subscribeClick((param) => {
      if (!param.point || !pickModeRef.current || !onPricePickRef.current) return;
      const price = candleSeries.coordinateToPrice(param.point.y);
      if (price != null && Number.isFinite(price)) {
        onPricePickRef.current(pickModeRef.current, Number(price.toFixed(4)));
      }
    });

    const resizeObserver = new ResizeObserver(() => {
      if (containerRef.current && chartRef.current) {
        chartRef.current.applyOptions({
          width: containerRef.current.clientWidth,
          height: containerRef.current.clientHeight,
        });
      }
    });
    resizeObserver.observe(containerRef.current);

    return () => {
      resizeObserver.disconnect();
      chart.remove();
      chartRef.current = null;
      candleSeriesRef.current = null;
      maSeriesRef.current = [];
      volumeSeriesRef.current = null;
      stopLineRef.current = null;
      entryLineRef.current = null;
      hasFitOnceRef.current = false;
    };
  }, []);

  useEffect(() => {
    maSeriesRef.current.forEach((series) => series.applyOptions({ visible: showMovingAverages }));
  }, [showMovingAverages]);

  useEffect(() => {
    const series = candleSeriesRef.current;
    if (!series) return;
    if (stopLineRef.current) series.removePriceLine(stopLineRef.current);
    // Both lines are reference marks, not the subject of the chart: thin and
    // muted so the candles stay the thing you read.
    stopLineRef.current = Number(stopPrice) > 0 ? series.createPriceLine({
      price: Number(stopPrice), color: 'rgba(217, 45, 32, 0.45)', lineWidth: 1,
      lineStyle: 2, axisLabelVisible: true, title: 'STOP',
    }) : null;

    // Where this position was actually entered. Only drawn for a holding you
    // clicked, so the screener does not get trade lines drawn over it.
    if (entryLineRef.current) series.removePriceLine(entryLineRef.current);
    entryLineRef.current = Number(holdingEntryPrice) > 0 ? series.createPriceLine({
      price: Number(holdingEntryPrice), color: 'rgba(2, 122, 72, 0.45)', lineWidth: 1,
      lineStyle: 2, axisLabelVisible: true, title: 'ENTRY',
    }) : null;
  }, [entryPrice, stopPrice, holdingEntryPrice]);

  // Load candle data into the existing series. No chart teardown here, so the
  // current zoom/pan position is left exactly as the user had it — lightweight-charts
  // only resets the view when we explicitly call fitContent().
  useEffect(() => {
    if (!market || !symbol || !date) return undefined;
    const controller = new AbortController();
    setStatus('loading');

    const load = async () => {
      try {
        const response = await apiFetch(
          `/api/screener/candles/${market}/${encodeURIComponent(symbol)}?date=${date}&period=${period}&dataset=${dataset}${live ? '&live=1' : ''}${fullHistory ? '&full=1' : ''}${reviewEndDate ? `&reviewEnd=${reviewEndDate}` : ''}${reviewStartDate ? `&reviewStart=${reviewStartDate}` : ''}`,
          { signal: controller.signal }
        );
        if (!response.ok) throw new Error('Unable to load candles');
        const payload = await response.json();
        if (!payload.candles?.length) throw new Error('No candle data');
        if (controller.signal.aborted || !candleSeriesRef.current) return;

        candleSeriesRef.current.setData(payload.candles.map((c) => ({
          time: c.time,
          open: c.open,
          high: c.high,
          low: c.low,
          close: c.close,
        })));

        maSeriesRef.current.forEach((series, index) => {
          // Averaged across the warm-up bars the backend sent, then clipped to
          // the visible window: without this the 50MA only begins 50 candles
          // into the chart.
          series.setData(movingAverageOverWarmup(payload, MA_CONFIG[index].period));
        });

        volumeSeriesRef.current?.setData(payload.candles
          .filter((c) => !c.temporary && Number.isFinite(Number(c.volume)))
          .map((c) => ({
            time: c.time,
            value: c.volume,
            color: c.close >= c.open ? '#12b76a66' : '#f0443866',
          })));

        if (!hasFitOnceRef.current) {
          chartRef.current?.timeScale().fitContent();
          hasFitOnceRef.current = true;
        } else {
          // Guard against the existing view pointing past the new data (e.g. a
          // symbol with a shorter history) — fall back to fitContent only then.
          const visibleRange = chartRef.current?.timeScale().getVisibleLogicalRange();
          if (visibleRange && visibleRange.from >= payload.candles.length) {
            chartRef.current?.timeScale().fitContent();
          }
        }

        setStatus('ready');
      } catch (error) {
        if (error.name !== 'AbortError') setStatus('error');
      }
    };

    load();
    // The screen rows are refreshed by us_screens during market hours. Re-read
    // only the active live chart as well, so its in-progress candle tracks the
    // same feed without changing the user's zoom or the table's EOD %Up.
    const refreshId = live ? window.setInterval(load, 60_000) : null;
    return () => {
      if (refreshId) window.clearInterval(refreshId);
      controller.abort();
    };
  }, [market, symbol, date, period, dataset, live, fullHistory, reviewEndDate, reviewStartDate]);

  return (
    <div className="chart-shell">
      <div className={`chart-canvas ${pickMode ? 'price-picking' : ''}`} ref={containerRef} />
      {symbol && status === 'loading' && <div className="chart-status">Loading candles…</div>}
      {symbol && status === 'error' && <div className="chart-status error">Chart data is unavailable</div>}
    </div>
  );
};

export default InteractiveChart;
